import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

// Definir los permisos disponibles
export const PERMISSIONS = {
  // Menú
  MENU_USER_MANAGEMENT: 'MENU_USER_MANAGEMENT',
  MENU_DEVICE_MANAGEMENT: 'MENU_DEVICE_MANAGEMENT',
  MENU_OPERATIONAL_PANEL: 'MENU_OPERATIONAL_PANEL',
  MENU_PLATFORM_MANAGEMENT: 'MENU_PLATFORM_MANAGEMENT',

  // Administración de Usuarios
  USER_CREATE: 'USER_CREATE',
  USER_READ: 'USER_READ',
  USER_UPDATE: 'USER_UPDATE',
  USER_DELETE: 'USER_DELETE',

  // Administración de Dispositivos
  DEVICE_CREATE: 'DEVICE_CREATE',
  DEVICE_READ_ALL: 'DEVICE_READ_ALL',
  DEVICE_READ_ASSOCIATED: 'DEVICE_READ_ASSOCIATED',
  DEVICE_UPDATE: 'DEVICE_UPDATE',
  DEVICE_UPDATE_READONLY: 'DEVICE_UPDATE_READONLY',
  DEVICE_DELETE: 'DEVICE_DELETE',
  DEVICE_ASSOCIATE: 'DEVICE_ASSOCIATE',
  DEVICE_UPDATE_SERIAL_NUMBER: 'DEVICE_UPDATE_SERIAL_NUMBER',
  DEVICE_EDIT_APP_VERSIONS: 'DEVICE_EDIT_APP_VERSIONS',

  // Panel Operativo
  OPERATIONAL_READ: 'OPERATIONAL_READ',
  OPERATIONAL_WRITE: 'OPERATIONAL_WRITE',

  // Lecturas
  LECTURE_INSERT: 'LECTURE_INSERT',
  LECTURE_BULK_INSERT: 'LECTURE_BULK_INSERT',
  LECTURE_IMPORT_FILE: 'LECTURE_IMPORT_FILE',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

const ROLES = {
  ADMINISTRADOR: 'Administrador',
  SUPERVISOR: 'Supervisor',
  OPERARIO: 'Operario',
} as const;

type Role = (typeof ROLES)[keyof typeof ROLES];

// Definir qué permisos tiene cada rol
const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [ROLES.ADMINISTRADOR]: [
    PERMISSIONS.MENU_USER_MANAGEMENT,
    PERMISSIONS.MENU_DEVICE_MANAGEMENT,
    PERMISSIONS.MENU_OPERATIONAL_PANEL,
    PERMISSIONS.MENU_PLATFORM_MANAGEMENT,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.DEVICE_CREATE,
    PERMISSIONS.DEVICE_READ_ALL,
    PERMISSIONS.DEVICE_UPDATE,
    PERMISSIONS.DEVICE_EDIT_APP_VERSIONS,
    PERMISSIONS.DEVICE_UPDATE_SERIAL_NUMBER,
    PERMISSIONS.DEVICE_DELETE,
    PERMISSIONS.DEVICE_ASSOCIATE,
    PERMISSIONS.OPERATIONAL_READ,
    PERMISSIONS.OPERATIONAL_WRITE,
    PERMISSIONS.LECTURE_INSERT,
    PERMISSIONS.LECTURE_BULK_INSERT,
    PERMISSIONS.LECTURE_IMPORT_FILE,
  ],
  [ROLES.SUPERVISOR]: [
    PERMISSIONS.MENU_DEVICE_MANAGEMENT,
    PERMISSIONS.MENU_OPERATIONAL_PANEL,
    PERMISSIONS.DEVICE_READ_ALL,
    PERMISSIONS.DEVICE_UPDATE,
    PERMISSIONS.OPERATIONAL_READ,
  ],
  [ROLES.OPERARIO]: [PERMISSIONS.MENU_OPERATIONAL_PANEL, PERMISSIONS.OPERATIONAL_READ],
};

const hasPermission = (userRole: Role, permission: Permission): boolean => {
  const userPermissions = ROLE_PERMISSIONS[userRole] || [];
  return userPermissions.includes(permission);
};

const getVisibleMenuItems = (userRole: Role): string[] => {
  const menuItems: string[] = [];

  if (hasPermission(userRole, PERMISSIONS.MENU_USER_MANAGEMENT)) {
    menuItems.push('users');
  }

  if (hasPermission(userRole, PERMISSIONS.MENU_DEVICE_MANAGEMENT)) {
    menuItems.push('devices');
  }

  if (hasPermission(userRole, PERMISSIONS.MENU_OPERATIONAL_PANEL)) {
    menuItems.push('operations');
  }

  if (hasPermission(userRole, PERMISSIONS.MENU_PLATFORM_MANAGEMENT)) {
    menuItems.push('platform');
  }

  if (hasPermission(userRole, PERMISSIONS.LECTURE_INSERT)) {
    menuItems.push('lectures/insert');
  }

  if (hasPermission(userRole, PERMISSIONS.LECTURE_BULK_INSERT)) {
    menuItems.push('lectures/bulk-insert');
  }

  if (hasPermission(userRole, PERMISSIONS.LECTURE_IMPORT_FILE)) {
    menuItems.push('lectures/import');
  }

  return menuItems;
};

interface PermissionsContextType {
  hasPermission: (permission: Permission) => boolean;
  getVisibleMenuItems: () => string[];
  userRole: string | null;
}

const PermissionsContext = createContext<PermissionsContextType | null>(null);

export const PermissionsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  const contextValue = useMemo(
    () => ({
      hasPermission: (permission: Permission) =>
        user ? hasPermission(user.role as Role, permission) : false,
      getVisibleMenuItems: () => (user ? getVisibleMenuItems(user.role as Role) : []),
      userRole: user?.role || null,
    }),
    [user]
  );

  return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
